/**
 * Firestore — diagnostics mode.
 *
 * Database   : sensei-diagnostics
 * Collection : /diagnostics/{sessionId}
 *
 * Document shape: DiagnosticsSession (see modes/diagnostics/types.ts)
 * This must stay in sync with:
 *   - firestore.sensei-diagnostics.rules
 *   - The React version (reactjs/src/lib/firestore-diagnostics.ts)
 *
 * Session ID format: `diag-${uid}-${Date.now()}`  (same as React)
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { getDiagnosticsDb } from "@/platform/firebase/client";
import type { DiagnosticsSession, DiagnosticQuestion } from "@/modes/diagnostics/types";

const COL = "diagnostics";

function col() { return collection(getDiagnosticsDb(), COL); }
function d(id: string) { return doc(getDiagnosticsDb(), COL, id); }

// ─── Session CRUD ─────────────────────────────────────────────────────────────

export interface CreateSessionInput {
  title:          string;
  diagnosticId:   string;
  diagnosticTitle: string;
  questions:      DiagnosticQuestion[];
}

/**
 * Create a new DiagnosticsSession in Firestore.
 * Session ID mirrors React: `diag-${uid}-${Date.now()}`
 * Returns the session ID.
 */
export async function createSession(
  uid:   string,
  input: CreateSessionInput
): Promise<string> {
  const now       = new Date().toISOString();
  const sessionId = `diag-${uid}-${Date.now()}`;
  const ref       = d(sessionId);

  const session: DiagnosticsSession = {
    id:               sessionId,
    title:            input.title,
    diagnosticId:     input.diagnosticId,
    diagnosticTitle:  input.diagnosticTitle,
    questions:        input.questions,
    answers:          {},
    answer_authors:   {},
    analysis:         null,
    analysisMarkdown: "",
    score:            0,
    completedAt:      now,
    isStarred:        false,
    isArchived:       false,
    isCompleted:      false,
    currentQuestion:  0,
    createdAt:        now,
    created_by_uid:   uid,
    shared_with:      [],
    invited_emails:   [],
    resolved_uids:    [],
  };

  await setDoc(ref, session);
  return sessionId;
}

/** Fetch a single session by ID */
export async function getSession(
  sessionId: string
): Promise<DiagnosticsSession | null> {
  const snap = await getDoc(d(sessionId));
  return snap.exists() ? (snap.data() as DiagnosticsSession) : null;
}

/** Fetch all sessions owned by a user, newest first */
export async function getUserSessions(uid: string): Promise<DiagnosticsSession[]> {
  const q    = query(col(), where("created_by_uid", "==", uid), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((doc) => doc.data() as DiagnosticsSession);
}

/** Fetch sessions where user was invited by email */
export async function getSessionsByInvitedEmail(email: string): Promise<DiagnosticsSession[]> {
  const q    = query(col(), where("invited_emails", "array-contains", email), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((doc) => doc.data() as DiagnosticsSession);
}

/** Fetch sessions where user is a resolved collaborator */
export async function getCollaboratorSessions(uid: string): Promise<DiagnosticsSession[]> {
  const q    = query(col(), where("resolved_uids", "array-contains", uid), orderBy("createdAt", "desc"));
  const snap = await getDocs(q);
  return snap.docs.map((doc) => doc.data() as DiagnosticsSession);
}

/** Partial update — used to persist answers, currentQuestion, score, analysis, etc. */
export async function updateSession(
  sessionId: string,
  data:      Partial<DiagnosticsSession>
): Promise<void> {
  await updateDoc(d(sessionId), data as Record<string, unknown>);
}

/** Update a single answer field — used by collaborators to record authorship */
export async function updateAnswerWithAuthor(
  sessionId:  string,
  questionId: string,
  value:      number,
  authorUid:  string
): Promise<void> {
  await updateDoc(d(sessionId), {
    [`answers.${questionId}`]:       value,
    [`answer_authors.${questionId}`]: authorUid,
  });
}

/** Delete a session (owner only — rules enforce this) */
export async function deleteSession(sessionId: string): Promise<void> {
  await deleteDoc(d(sessionId));
}

// ─── Collaborators ────────────────────────────────────────────────────────────

export async function addCollaborator(sessionId: string, email: string): Promise<void> {
  await updateDoc(d(sessionId), {
    shared_with:    arrayUnion({ email, uid: null }),
    invited_emails: arrayUnion(email),
  });
}

export async function resolveCollaboratorUid(
  sessionId: string,
  email:     string,
  uid:       string
): Promise<void> {
  // Two sequential writes — arrayRemove + arrayUnion can't target same field in one call
  await updateDoc(d(sessionId), { shared_with: arrayRemove({ email, uid: null }) });
  await updateDoc(d(sessionId), {
    shared_with:   arrayUnion({ email, uid }),
    resolved_uids: arrayUnion(uid),
  });
}

export async function removeCollaborator(sessionId: string, email: string): Promise<void> {
  // Read-then-write to remove the exact collaborator object
  const snap = await getDoc(d(sessionId));
  if (!snap.exists()) return;
  const data    = snap.data() as DiagnosticsSession;
  const entry   = (data.shared_with ?? []).find((c) => c.email === email);
  if (!entry) return;

  const updates: Record<string, unknown> = {
    shared_with:    arrayRemove(entry),
    invited_emails: arrayRemove(email),
  };
  if (entry.uid !== null) {
    updates.resolved_uids = arrayRemove(entry.uid);
  }
  await updateDoc(d(sessionId), updates);
}

/** Count how many diagnostics a user has created (for free-plan gating) */
export async function countUserDiagnostics(uid: string): Promise<number> {
  const q    = query(col(), where("created_by_uid", "==", uid));
  const snap = await getDocs(q);
  return snap.size;
}

// ─── Share Links ─────────────────────────────────────────────────────────────

/**
 * Generate (or return existing) share token for a session.
 * Idempotent — if a token already exists, returns it without overwriting.
 */
export async function generateShareToken(sessionId: string): Promise<string> {
  const snap = await getDoc(d(sessionId));
  if (!snap.exists()) throw new Error("Session not found");

  const data = snap.data() as DiagnosticsSession;
  if (data.share_token) return data.share_token;

  const token = crypto.randomUUID();
  await updateDoc(d(sessionId), { share_token: token });
  return token;
}

/**
 * Regenerate share token — invalidates any previous share links.
 */
export async function regenerateShareToken(sessionId: string): Promise<string> {
  const token = crypto.randomUUID();
  await updateDoc(d(sessionId), { share_token: token });
  return token;
}

/**
 * Fetch a session by its share token.
 * Returns null if no session has this token.
 */
export async function getSessionByShareToken(token: string): Promise<DiagnosticsSession | null> {
  const q    = query(col(), where("share_token", "==", token));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  return snap.docs[0].data() as DiagnosticsSession;
}
